import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { isSmallerSelector } from '~/components/layout/mediaquery';
import {
	GameHover,
	GameHoverTop,
	GameHoverBottom,
	GameName,
	ProviderName,
	PlayButton
} from '~/components/casino-shared/game-card/game-card-styles';
import EnhancedImage, { Image } from '~/components/casino-shared/tournaments/enhanced-image';
import useTranslationsByTypedArray from '~/components/casino-shared/tournaments/utils/use-translations-by-typed-array';
import useTimeTicker from '~/components/casino-shared/tournaments/utils/use-time-ticker';
import useConditionalPrizePool from '~/components/casino-shared/tournaments/utils/use-conditional-prize-pool';
import useTournamentTags from '~/components/casino-shared/tournaments/utils/use-tournament-tags';
import usePushTransformedPath from '~/components/casino-shared/tournaments/utils/use-push-transformed-path';
import trackTournamentEvent from '~/components/casino-shared/tournaments/utils/track-tournament-event';
import {
	preload,
	loadTournamentDetails,
	loadTournamentLobby,
	loadTournamentsQueryClientProvider,
	loadTournamentDetailsLeaderboard
} from '~/components/casino-shared/tournaments/async';
import breakpoint from '~/utils/style-utils';
import { ROUTE_TOURNAMENT_LOBBY } from '~/components/casino-shared/tournaments/routes';
import { useCallback } from 'react';
import Icon, { ICON_USER_FILLED, ICON_CLOCK_LINE } from '~/components/icon';
import TournamentCardCounter from './tournament-card-counter';

const tournamentCardTextKeys = ['TOURNAMENTS_CARD_HOVER_BUTTON_TEXT', 'TOURNAMENTS_TIME_ACTIVE_NOW'] as const;

export const TOURNAMENT_CARD_WIDTH = 336;
export const TOURNAMENT_CARD_HEIGHT = 162;

const ImageContainer = styled.div<{ rotated?: boolean }>`
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	transform: ${({ rotated }) => (rotated ? 'scaleX(-1)' : 'none')};
`;

const Container = styled.div`
	position: relative;
	display: flex;
	width: ${TOURNAMENT_CARD_WIDTH}px;
	height: ${TOURNAMENT_CARD_HEIGHT}px;
	border-radius: 12px;
	overflow: hidden;

	img {
		pointer-events: none;
		touch-action: none;
	}

	${breakpoint.lg`
		&:hover ${GameHover} {
			z-index: 0;
			display: flex;
			background: rgba(0, 0, 0, 0.4);
			backdrop-filter: blur(25px);
		}
	`}
`;

const ContentCell = styled.div`
	width: 50%;
	position: relative;
`;

const ContentCellBackgroundOverlay = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(0, 0, 0, 0.3);
	backdrop-filter: blur(50px);
`;

const ContentCellInner = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	display: flex;
	flex-direction: column;
	padding: 12px;
`;

const ContentCellTop = styled.div`
	display: flex;
	justify-content: flex-start;
`;

const ContentCellBottom = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
	justify-content: flex-end;
	gap: 4px;
`;

const Status = styled.div<{ hasStarted: boolean }>`
	display: flex;
	align-items: center;
	background: ${({ hasStarted }) => (hasStarted ? '#fff' : 'unset')};
	border: ${({ hasStarted }) => (!hasStarted ? 'solid 1px #fff' : 'unset')};
	padding: 3px;
	border-radius: 100px;
`;

const StatusCell = styled.div<{ altColor?: boolean }>`
	font: var(--tournaments-small-bold, var(--small-bold));
	color: ${({ altColor }) => (altColor ? '#fff' : 'var(--main-text-dark)')};
	padding: 0 3px;
`;

const StatusIconContainer = styled.div`
	svg {
		width: 14px;
		height: 14px;
		color: var(--main-text-dark);
	}
`;

const StatusNumber = styled.div`
	font: var(--tournaments-small-bold, var(--small-bold));
	color: var(--main-text-dark);
`;

const Prize = styled.div`
	font: var(--h2);
	color: var(--tournaments-text-on-tag);
`;

const Name = styled.div`
	font: var(--tournaments-caption, var(--label-caption));
	color: var(--tournaments-text-on-tag);
`;

const Tags = styled.div`
	opacity: 0.6;
	font: var(--tournaments-caption, var(--label-caption));
	color: var(--tournaments-text-on-tag);
`;

const ImageCell = styled.div`
	width: 50%;
	position: relative;
`;

interface TournamentCardProps {
	id: number;
	name: string;
	image: Image;
	otherView?: Image;
	tags: string[];
	prizePool?: number;
	prizePoolText?: string;
	currencyCode: string;
	startDate: string;
	endDate: string;
	isJoined: boolean;
	playerCount: number;
}

export default function TournamentCard({
	id,
	name,
	image,
	otherView,
	tags,
	prizePool,
	prizePoolText,
	currencyCode,
	startDate,
	endDate,
	isJoined,
	playerCount
}: TournamentCardProps) {
	const { hasStarted, hasEnded, isEndingToday, timeText } = useTimeTicker(startDate, endDate);
	const conditionalPrizePool = useConditionalPrizePool(prizePoolText, prizePool, currencyCode);
	const tournamentTags = useTournamentTags(tags);
	const { translations } = useTranslationsByTypedArray(tournamentCardTextKeys);
	const isSmall = useSelector(isSmallerSelector);
	const pushTransformedPath = usePushTransformedPath();

	const goDetails = () => {
		trackTournamentEvent('explore-page', 'tournament-card-click', {
			tournamentName: name,
			tournamentPrize: conditionalPrizePool,
			isPlayerJoined: isJoined
		});

		pushTransformedPath(`${ROUTE_TOURNAMENT_LOBBY}/${id}`);
	};

	const preloadDetails = () => {
		preload(
			loadTournamentLobby(),
			loadTournamentsQueryClientProvider(),
			loadTournamentDetails(),
			loadTournamentDetailsLeaderboard()
		);
	};

	const renderImage = useCallback(
		(rotated?: boolean) => (
			<ImageContainer rotated={rotated}>
				<EnhancedImage
					image={image}
					otherView={otherView}
					alt={name}
					sizes={`${TOURNAMENT_CARD_WIDTH / 2}px`}
				/>
			</ImageContainer>
		),
		[image, otherView, name]
	);

	return (
		<Container
			onClick={isSmall ? goDetails : undefined}
			onMouseEnter={preloadDetails}
			onTouchStart={preloadDetails}
			data-at="tournament-card"
		>
			<ContentCell>
				{renderImage(true)}

				<ContentCellBackgroundOverlay />

				<ContentCellInner>
					<ContentCellTop>
						<Status hasStarted={hasStarted}>
							{hasStarted ? (
								isEndingToday || hasEnded ? (
									<>
										<StatusIconContainer>
											<Icon type={ICON_CLOCK_LINE} />
										</StatusIconContainer>

										<StatusCell data-at="time-text">{timeText}</StatusCell>
									</>
								) : playerCount < 50 ? (
									<StatusCell data-at="time-text">
										{translations.TOURNAMENTS_TIME_ACTIVE_NOW}
									</StatusCell>
								) : (
									<>
										<StatusCell data-at="time-text">{timeText}</StatusCell>

										<StatusIconContainer>
											<Icon type={ICON_USER_FILLED} />
										</StatusIconContainer>

										<StatusCell>
											<TournamentCardCounter number={playerCount} NumberWrapper={StatusNumber} />
										</StatusCell>
									</>
								)
							) : (
								<StatusCell altColor data-at="time-text">
									{timeText}
								</StatusCell>
							)}
						</Status>
					</ContentCellTop>

					<ContentCellBottom>
						<Prize data-at="prize-pool">{conditionalPrizePool}</Prize>

						<Name data-at="name">{name}</Name>

						<Tags data-at="tags">{tournamentTags}</Tags>
					</ContentCellBottom>
				</ContentCellInner>
			</ContentCell>

			<ImageCell>{renderImage()}</ImageCell>

			<GameHover data-at="hover">
				<GameHoverTop>
					<GameName data-at="name">{name}</GameName>
					<ProviderName data-at="tags">{tournamentTags}</ProviderName>
				</GameHoverTop>

				<GameHoverBottom>
					<PlayButton onClick={goDetails} data-at="details-button">
						{translations.TOURNAMENTS_CARD_HOVER_BUTTON_TEXT}
					</PlayButton>
				</GameHoverBottom>
			</GameHover>
		</Container>
	);
}
